import { useState } from "react";
import { Box, Typography, TextField, Tooltip, alpha } from "@mui/material";

// All available icons for categories
import LocalDiningIcon from "@mui/icons-material/LocalDining";
import PsychologyIcon from "@mui/icons-material/Psychology";
import PsychologyAltIcon from "@mui/icons-material/PsychologyAlt";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import FavoriteIcon from "@mui/icons-material/Favorite";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import StarIcon from "@mui/icons-material/Star";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import SoupKitchenIcon from "@mui/icons-material/SoupKitchen";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import Diversity1Icon from "@mui/icons-material/Diversity1";
import HandshakeIcon from "@mui/icons-material/Handshake";
import SportsIcon from "@mui/icons-material/Sports";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import HealingIcon from "@mui/icons-material/Healing";
import CategoryIcon from "@mui/icons-material/Category";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import SpaIcon from "@mui/icons-material/Spa";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import FaceRetouchingNaturalIcon from "@mui/icons-material/FaceRetouchingNatural";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import SchoolIcon from "@mui/icons-material/School";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import NightlightIcon from "@mui/icons-material/Nightlight";
import CakeIcon from "@mui/icons-material/Cake";
import LocalCafeIcon from "@mui/icons-material/LocalCafe";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import BoltIcon from "@mui/icons-material/Bolt";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

// Map: string key → React component
export const ICON_MAP = {
  LocalDining: LocalDiningIcon,
  Psychology: PsychologyIcon,
  PsychologyAlt: PsychologyAltIcon,
  FitnessCenter: FitnessCenterIcon,
  HealthAndSafety: HealthAndSafetyIcon,
  MonitorHeart: MonitorHeartIcon,
  Favorite: FavoriteIcon,
  MedicalInformation: MedicalInformationIcon,
  AdminPanelSettings: AdminPanelSettingsIcon,
  Star: StarIcon,
  VolunteerActivism: VolunteerActivismIcon,
  SoupKitchen: SoupKitchenIcon,
  PersonSearch: PersonSearchIcon,
  Diversity1: Diversity1Icon,
  Handshake: HandshakeIcon,
  Sports: SportsIcon,
  GroupAdd: GroupAddIcon,
  Healing: HealingIcon,
  Category: CategoryIcon,
  SelfImprovement: SelfImprovementIcon,
  Spa: SpaIcon,
  ChildCare: ChildCareIcon,
  FaceRetouchingNatural: FaceRetouchingNaturalIcon,
  EmojiPeople: EmojiPeopleIcon,
  DirectionsRun: DirectionsRunIcon,
  MusicNote: MusicNoteIcon,
  AutoAwesome: AutoAwesomeIcon,
  Lightbulb: LightbulbIcon,
  School: SchoolIcon,
  LocalFlorist: LocalFloristIcon,
  WbSunny: WbSunnyIcon,
  Nightlight: NightlightIcon,
  Cake: CakeIcon,
  LocalCafe: LocalCafeIcon,
  Restaurant: RestaurantIcon,
  Bolt: BoltIcon,
  Whatshot: WhatshotIcon,
  EmojiEvents: EmojiEventsIcon,
  WorkspacePremium: WorkspacePremiumIcon,
};

// Labels for display
const ICON_LABELS = {
  LocalDining: "Comida",
  Psychology: "Psicología",
  PsychologyAlt: "Psiquiatría",
  FitnessCenter: "Fitness",
  HealthAndSafety: "Salud",
  MonitorHeart: "Corazón",
  Favorite: "Favorito",
  MedicalInformation: "Medicina",
  AdminPanelSettings: "Admin",
  Star: "Estrella",
  VolunteerActivism: "Voluntariado",
  SoupKitchen: "Cocina",
  PersonSearch: "Persona",
  Diversity1: "Diversidad",
  Handshake: "Duplex",
  Sports: "Deporte",
  GroupAdd: "Grupo",
  Healing: "Curación",
  Category: "Categoría",
  SelfImprovement: "Meditación",
  Spa: "Spa",
  ChildCare: "Niños",
  FaceRetouchingNatural: "Belleza",
  EmojiPeople: "Personas",
  DirectionsRun: "Correr",
  MusicNote: "Música",
  AutoAwesome: "Magia",
  Lightbulb: "Idea",
  School: "Educación",
  LocalFlorist: "Flor",
  WbSunny: "Sol",
  Nightlight: "Noche",
  Cake: "Pastel",
  LocalCafe: "Café",
  Restaurant: "Restaurant",
  Bolt: "Rayo",
  Whatshot: "Fuego",
  EmojiEvents: "Trofeo",
  WorkspacePremium: "Premium",
};

const IconPicker = ({ selected, onSelect, color }) => {
  const [search, setSearch] = useState("");
  const iconKeys = Object.keys(ICON_MAP);
  const filtered = search
    ? iconKeys.filter(
        (key) =>
          key.toLowerCase().includes(search.toLowerCase()) ||
          (ICON_LABELS[key] || "").toLowerCase().includes(search.toLowerCase())
      )
    : iconKeys;

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, mb: 1, display: "block", color: "text.secondary" }}
      >
        Icono
      </Typography>
      <TextField
        size="small"
        fullWidth
        placeholder="Buscar icono..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 1.5 }}
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 0.75,
          maxHeight: 200,
          overflowY: "auto",
          p: 0.5,
        }}
      >
        {filtered.map((key) => {
          const IconComp = ICON_MAP[key];
          const isSelected = selected === key;
          return (
            <Tooltip key={key} title={ICON_LABELS[key] || key} placement="top">
              <Box
                onClick={() => onSelect(key)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  cursor: "pointer",
                  border: isSelected ? `2px solid ${color || "#D0749B"}` : "2px solid transparent",
                  bgcolor: isSelected ? alpha(color || "#D0749B", 0.12) : "transparent",
                  transition: "all 0.15s",
                  "&:hover": {
                    bgcolor: alpha(color || "#D0749B", 0.08),
                  },
                }}
              >
                <IconComp
                  sx={{
                    fontSize: 22,
                    color: isSelected ? color || "#D0749B" : "text.secondary",
                  }}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

export default IconPicker;
